'use client';

import {
  Suggestion,
  buildRecentQueriesList,
  buildSearchBox,
} from '@coveo/atomic-react';
import { useController } from '@lib/hooks/useController';
import { MagnifyingGlass, X } from 'phosphor-icons';
import {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { c } from 'ui-components';
import { engine } from '../Engine';
import { useSearchQueryUrl } from '../hooks/useSearchQueryUrl';
import {
  Controllers,
  HeaderSearchProps,
  SuggestionItem,
} from './HeaderSearch.types';

/**
 * Standalone header search for non-search pages.
 * Renders a simple form that navigates to /search on submit (query-string URL
 * on the SSR search path, hash URL on the CSR path — see useSearchQueryUrl).
 * No Coveo engine connection needed.
 */
const StandaloneHeaderSearch = ({ placeholder }: { placeholder?: string }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const buildSearchQueryUrl = useSearchQueryUrl();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      window.location.href = buildSearchQueryUrl(query.trim());
    }
  };

  const handleClear = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <form onSubmit={handleSubmit} role="search" data-test="header-search-form">
      <fieldset className="flex items-stretch overflow-hidden rounded-md border border-grey-600 bg-grey-900 focus-within:border-transparent focus-within:bg-grey-800 focus-within:ring-2 focus-within:ring-blue-hover">
        <legend className="sr-only">{placeholder}</legend>
        <div className="relative flex flex-1 items-center">
          <input
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            type="text"
            placeholder={placeholder}
            className="block w-full bg-transparent px-3 py-2 font-medium text-white placeholder:text-grey-400 focus:outline-none"
            ref={inputRef}
            autoComplete="off"
          />
          <button
            type="button"
            className={c('mr-2 shrink-0 p-1 text-grey-400 hover:text-white', {
              hidden: query.length === 0,
            })}
            onClick={handleClear}
            aria-label="Clear search"
          >
            <X height={15} width={15} />
          </button>
        </div>
        <button
          type="submit"
          className="flex w-10 shrink-0 items-center justify-center bg-blue hover:bg-blue-hover"
          aria-label="Search"
        >
          <MagnifyingGlass width={16} height={16} className="text-white" />
        </button>
      </fieldset>
    </form>
  );
};

/**
 * Header search box that connects to the SearchV2 Headless engine.
 * Provides query suggestions and recent queries, matching the
 * Atomic SearchBox feature set used on /search.
 *
 * When `standalone` is true, renders a simple search form that navigates
 * to /search on submit (used on non-search pages where the engine isn't initialized).
 */
const MAX_CONNECT_ATTEMPTS = 20;

export const HeaderSearch = ({
  placeholder,
  standalone,
}: HeaderSearchProps) => {
  const [controllers, setControllers] = useState<Controllers | null>(null);

  useEffect(() => {
    if (standalone) return;

    let cancelled = false;
    let attempt = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const tryConnect = () => {
      try {
        const eng = engine();
        const searchBox = buildSearchBox(eng, {
          options: { numberOfSuggestions: 5 },
        });
        const recentQueries = buildRecentQueriesList(eng, {
          options: { maxLength: 5 },
        });
        if (!cancelled) {
          setControllers({ searchBox, recentQueries });
        }
      } catch {
        attempt += 1;
        if (!cancelled && attempt < MAX_CONNECT_ATTEMPTS) {
          const delay = Math.min(50 * 2 ** attempt, 2000);
          timeoutId = setTimeout(tryConnect, delay);
        }
      }
    };
    tryConnect();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [standalone]);

  if (standalone) {
    return <StandaloneHeaderSearch placeholder={placeholder} />;
  }

  if (!controllers) {
    return (
      <fieldset className="flex items-stretch overflow-hidden rounded-md border border-grey-600 bg-grey-900">
        <legend className="sr-only">{placeholder}</legend>
        <input
          type="text"
          placeholder={placeholder}
          className="block w-full bg-transparent px-3 py-2 font-medium text-white placeholder:text-grey-400"
          disabled
        />
        <div className="flex w-10 shrink-0 items-center justify-center bg-blue">
          <MagnifyingGlass width={16} height={16} className="text-white" />
        </div>
      </fieldset>
    );
  }

  return (
    <HeaderSearchInner controllers={controllers} placeholder={placeholder} />
  );
};

type HeaderSearchInnerProps = {
  controllers: Controllers;
  placeholder?: string;
};

const HeaderSearchInner = ({
  controllers,
  placeholder,
}: HeaderSearchInnerProps) => {
  const { searchBox, recentQueries } = controllers;
  const searchBoxState = useController(searchBox);
  const recentQueriesState = useController(recentQueries);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Build the unified list of suggestions
  const items = useMemo((): SuggestionItem[] => {
    const list: SuggestionItem[] = [];

    // When no query text, show recent queries
    if (
      searchBoxState.value.length === 0 &&
      recentQueriesState.queries.length > 0
    ) {
      recentQueriesState.queries.forEach((query, index) => {
        list.push({ type: 'recent', rawValue: query, index });
      });
    }

    // Always show query suggestions when available
    if (searchBoxState.suggestions.length > 0) {
      searchBoxState.suggestions.forEach((s: Suggestion) => {
        list.push({
          type: 'suggestion',
          rawValue: s.rawValue,
          highlightedValue: s.highlightedValue,
        });
      });
    }

    return list;
  }, [
    searchBoxState.value,
    searchBoxState.suggestions,
    recentQueriesState.queries,
  ]);

  const shouldShowDropdown = isOpen && items.length > 0;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    searchBox.submit();
    setIsOpen(false);
  };

  const handleClear = () => {
    searchBox.clear();
    searchBox.submit();
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleInputChange = (value: string) => {
    searchBox.updateText(value);
    setActiveIndex(-1);
    setIsOpen(true);
  };

  const handleFocus = () => {
    if (searchBoxState.value.length === 0) {
      // Show recent queries on focus with empty input
      setIsOpen(true);
    } else {
      searchBox.showSuggestions();
      setIsOpen(true);
    }
  };

  const selectItem = useCallback(
    (item: SuggestionItem) => {
      if (item.type === 'recent' && item.index !== undefined) {
        recentQueries.executeRecentQuery(item.index);
      } else {
        searchBox.selectSuggestion(item.rawValue);
      }
      setIsOpen(false);
      setActiveIndex(-1);
    },
    [searchBox, recentQueries]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!shouldShowDropdown) {
      if (e.key === 'ArrowDown') {
        handleFocus();
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < items.length) {
          e.preventDefault();
          selectItem(items[activeIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  const showClearButton = searchBoxState.value.length > 0;

  const renderSuggestionText = (item: SuggestionItem) => {
    if (item.type === 'suggestion' && item.highlightedValue) {
      // Coveo highlightedValue wraps matching text in <b> tags.
      // Parse into React elements for safe rendering.
      const parts = item.highlightedValue.split(/(<b>.*?<\/b>)/g);
      return (
        <span>
          {parts.map((part, i) => {
            const match = part.match(/^<b>(.*?)<\/b>$/);
            if (match) {
              return <b key={i}>{match[1]}</b>;
            }
            return part;
          })}
        </span>
      );
    }
    return <span>{item.rawValue}</span>;
  };

  return (
    <div ref={containerRef} className="relative">
      <form
        onSubmit={handleSubmit}
        role="search"
        data-test="header-search-form"
      >
        <fieldset className="flex items-stretch overflow-hidden rounded-md border border-grey-600 bg-grey-900 focus-within:border-transparent focus-within:bg-grey-800 focus-within:ring-2 focus-within:ring-blue-hover">
          <legend className="sr-only">{placeholder}</legend>
          <div className="relative flex flex-1 items-center">
            <input
              onChange={(e) => handleInputChange(e.target.value)}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              value={searchBoxState.value}
              type="text"
              placeholder={placeholder}
              className="block w-full bg-transparent px-3 py-2 font-medium text-white placeholder:text-grey-400 focus:outline-none"
              ref={inputRef}
              role="combobox"
              aria-expanded={shouldShowDropdown}
              aria-controls="header-search-suggestions"
              aria-activedescendant={
                activeIndex >= 0
                  ? `header-search-suggestion-${activeIndex}`
                  : undefined
              }
              autoComplete="off"
            />
            <button
              type="button"
              className={c('mr-2 shrink-0 p-1 text-grey-400 hover:text-white', {
                hidden: !showClearButton,
              })}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <X height={15} width={15} />
            </button>
          </div>
          <button
            type="submit"
            className="flex w-10 shrink-0 items-center justify-center bg-blue hover:bg-blue-hover"
            aria-label="Search"
          >
            <MagnifyingGlass width={16} height={16} className="text-white" />
          </button>
        </fieldset>
      </form>

      {shouldShowDropdown && (
        <div
          id="header-search-suggestions"
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-md border border-grey-700 bg-grey-800"
        >
          {items.map((item, i) => (
            <div
              key={`${item.type}-${item.rawValue}`}
              id={`header-search-suggestion-${i}`}
              role="option"
              tabIndex={-1}
              aria-selected={i === activeIndex}
              className={c(
                'flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm text-grey-300',
                {
                  'bg-grey-900': i === activeIndex,
                  'hover:bg-grey-900': i !== activeIndex,
                }
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                selectItem(item);
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <MagnifyingGlass
                width={14}
                height={14}
                className="shrink-0 text-grey-500"
              />
              {renderSuggestionText(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
